import { api } from '../../services/apiService';
import { hardwareApi } from '../../services/hardwareApi';

/**
 * THE UNLOCK-CODE EMAIL — "Your Ruby Till is on its way — your unlock code".
 *
 * One sender for both places that send it (the handover's Lock for shipping
 * path and the terminal Control tab's Resend / New code), so the two can never
 * drift on template ids, data shape or category.
 *
 * Owner email first, then the RubyTill admin copy, then `emailed` is stamped on
 * the order (history "Unlock code emailed to a•••@x.com" on every unit). The
 * stamp only follows a send that was accepted — a history row claiming an email
 * that never left is worse than none. The admin copy is best-effort.
 */

declare const __PRODUCT_CONFIG__: { adminEmail?: string } | undefined;

/** RubyTill's own team inbox, from the product config baked into the build. */
const adminInbox = (): string | undefined => {
  try {
    return typeof __PRODUCT_CONFIG__ !== 'undefined' ? __PRODUCT_CONFIG__?.adminEmail || undefined : undefined;
  } catch {
    return undefined;
  }
};

export interface UnlockCodeEmail {
  orderId: string;
  to: string;
  code: string;
  ownerName?: string;
  businessName?: string;
  locationName?: string;
  orderNumber?: string;
  /** Only on the handover — the owner's PIN travels in the same email. */
  pin?: string;
  devices: { label: string; registerName?: string }[];
}

/**
 * Throws only when the OWNER send fails. `stamped` is false when the email went
 * out but the order's `emailed` record did not save — the caller says so in
 * those words rather than claiming the email failed.
 */
export async function sendUnlockCodeEmails(e: UnlockCodeEmail): Promise<{ stamped: boolean }> {
  const shared = {
    ownerName: e.ownerName || '',
    businessName: e.businessName || '',
    locationName: e.locationName || '',
    code: e.code,
    devices: e.devices.map((d) => ({ label: d.label, ...(d.registerName ? { registerName: d.registerName } : {}) })),
    orderNumber: e.orderNumber || '',
    // The admin copy's "Open order" button deep-links on it (email-service superAdminOrderUrl).
    orderId: e.orderId,
  };
  await api.email.send({
    to: [e.to], templateId: 'device-unlock-code-owner', category: 'security',
    templateData: { ...shared, ...(e.pin ? { pin: e.pin } : {}) },
  });

  const admin = adminInbox();
  if (admin) {
    try {
      // The PIN is the owner's alone — it never goes to the team inbox.
      await api.email.send({ to: [admin], templateId: 'device-unlock-code-admin', templateData: shared, category: 'security' });
    } catch { /* the owner has the code; the team copy is a courtesy */ }
  }

  try {
    await hardwareApi.markOrderWelcomeEmailed(e.orderId, e.to);
    return { stamped: true };
  } catch {
    return { stamped: false };
  }
}
